import { useState } from 'react';
import {
  MAX_JOB_FOLDER_NAME_LENGTH,
  createJobFolderSchema,
  type JobFolder,
} from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  useCreateJobFolder,
  useDeleteJobFolder,
  useRenameJobFolder,
} from '@/hooks/useJobFolders';

// Create, rename and delete for job folders. Three small dialogs over the same
// name field, sharing the shared schema's validation so the client and the
// route can't disagree about what a folder may be called.

/** The one field both create and rename edit. */
function FolderNameField({
  value,
  onChange,
  error,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  onSubmit: () => void;
}) {
  return (
    <div className="py-4">
      <label htmlFor="job-folder-name" className="mb-1 block text-sm font-medium text-gray-700">
        Name
      </label>
      <Input
        id="job-folder-name"
        value={value}
        maxLength={MAX_JOB_FOLDER_NAME_LENGTH}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        autoFocus
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/** Validate through the shared schema; returns the trimmed name or an error. */
function parseName(raw: string): { name: string } | { error: string } {
  const parsed = createJobFolderSchema.safeParse({ name: raw });
  if (!parsed.success) {
    return { error: parsed.error.issues[0]?.message ?? 'Name the folder.' };
  }
  return { name: parsed.data.name };
}

export function JobFolderCreateDialog({
  jobId,
  open,
  parentFolderId,
  parentName,
  onClose,
}: {
  jobId: string;
  open: boolean;
  /** The folder the new one goes into; omitted for a root folder. */
  parentFolderId?: string;
  parentName?: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {open && (
        <DialogContent className="max-w-sm">
          {/* Keyed on the parent so reopening in a different folder starts
              clean rather than carrying the last name across. */}
          <CreateForm
            key={parentFolderId ?? 'root'}
            jobId={jobId}
            onClose={onClose}
            {...(parentFolderId ? { parentFolderId } : {})}
            {...(parentName ? { parentName } : {})}
          />
        </DialogContent>
      )}
    </Dialog>
  );
}

function CreateForm({
  jobId,
  parentFolderId,
  parentName,
  onClose,
}: {
  jobId: string;
  parentFolderId?: string;
  parentName?: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: createFolder, isPending } = useCreateJobFolder(jobId);

  const submit = async () => {
    const parsed = parseName(name);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);
    try {
      await createFolder({
        name: parsed.name,
        ...(parentFolderId ? { parentFolderId } : {}),
      });
      onClose();
    } catch {
      // The hook toasted. Staying open keeps the typed name on screen — most
      // failures here are a name already in use, which is one edit away.
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>New folder</DialogTitle>
        <DialogDescription>
          {parentName ? `Inside ${parentName}.` : 'Folders live directly under the job.'}
        </DialogDescription>
      </DialogHeader>

      <FolderNameField value={name} onChange={setName} error={error} onSubmit={submit} />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="button" onClick={submit} disabled={isPending}>
          {isPending ? 'Creating…' : 'Create'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function JobFolderRenameDialog({
  jobId,
  folder,
  onClose,
}: {
  jobId: string;
  folder: JobFolder | null;
  onClose: () => void;
}) {
  // A system folder is found by slug, so renaming it would break the lookup.
  const editable = folder && folder.slug === null ? folder : null;

  return (
    <Dialog open={editable != null} onOpenChange={(next) => !next && onClose()}>
      {editable && (
        <DialogContent className="max-w-sm">
          {/* Keyed on the folder so reopening on a different one rebuilds the
              field rather than carrying the last name across. */}
          <RenameForm key={editable.id} jobId={jobId} folder={editable} onClose={onClose} />
        </DialogContent>
      )}
    </Dialog>
  );
}

function RenameForm({
  jobId,
  folder,
  onClose,
}: {
  jobId: string;
  folder: JobFolder;
  onClose: () => void;
}) {
  const [name, setName] = useState(folder.name);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: renameFolder, isPending } = useRenameJobFolder(jobId);

  const submit = async () => {
    const parsed = parseName(name);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setError(null);

    if (parsed.name === folder.name) {
      onClose();
      return;
    }

    try {
      await renameFolder({ id: folder.id, input: { name: parsed.name } });
      onClose();
    } catch {
      // Toasted by the hook; the typed name stays put.
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Rename folder</DialogTitle>
      </DialogHeader>

      <FolderNameField value={name} onChange={setName} error={error} onSubmit={submit} />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="button" onClick={submit} disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
}

/**
 * What deleting this folder actually does, said plainly.
 *
 * "Delete folder" reads as "delete contents" to most people, and here it does
 * not: the files survive and move up one level. Subfolders do not survive, so
 * that gets said first.
 *
 * The counts are the folder's own — the server floats the whole subtree, so a
 * folder whose files all live in its children reads "Any files inside them
 * move to …", which is accurate without a recursive count on the wire.
 */
function describeDeletion(folder: JobFolder, parentName: string | undefined): string {
  const destination = parentName ?? 'All files';

  if (folder.fileCount === 0 && folder.subfolderCount === 0) return 'The folder is empty.';

  const sentences: string[] = [];
  if (folder.subfolderCount > 0) {
    sentences.push(
      `Its ${folder.subfolderCount} ${
        folder.subfolderCount === 1 ? 'subfolder is' : 'subfolders are'
      } deleted too.`,
    );
  }
  if (folder.fileCount > 0) {
    sentences.push(
      `${folder.fileCount} ${folder.fileCount === 1 ? 'file is' : 'files are'} not deleted — ` +
        `they move to ${destination}.`,
    );
  } else {
    sentences.push(`Any files inside them move to ${destination}.`);
  }

  return sentences.join(' ');
}

export function JobFolderDeleteDialog({
  jobId,
  folder,
  parentName,
  onClose,
}: {
  jobId: string;
  folder: JobFolder | null;
  /** The folder the files land in when this one is a subfolder. */
  parentName?: string;
  onClose: () => void;
}) {
  const { mutateAsync: deleteFolder, isPending } = useDeleteJobFolder(jobId);

  // Same reasoning as rename: a system folder can't go.
  const deletable = folder && folder.slug === null ? folder : null;

  const confirm = async () => {
    if (!deletable) return;
    try {
      await deleteFolder(deletable.id);
      onClose();
    } catch {
      // Toasted by the hook.
    }
  };

  return (
    <Dialog open={deletable != null} onOpenChange={(next) => !next && onClose()}>
      {deletable && (
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete “{deletable.name}”?</DialogTitle>
            <DialogDescription>{describeDeletion(deletable, parentName)}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirm} disabled={isPending}>
              {isPending ? 'Deleting…' : 'Delete folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}
